import { CommonModule } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { cloneDeep } from '@apollo/client/utilities';
import { MsalService } from '@azure/msal-angular';
import { UserService } from '../../../platform/services/user-service/userservice.service';
import { MSGraphAPIService } from '../../../platform/services/msgraphapiservice/msgraph-api.service';
import { environment } from '../../../../environments/environment';
import { User } from '../../../platform/interfaces/user';
import { Country } from '../../../platform/interfaces/country';
import { District } from '../../../platform/interfaces/district';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { matEdit } from '@ng-icons/material-icons/baseline';
import { matCloseOutline, matErrorOutline } from '@ng-icons/material-icons/outline';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ModalState } from '../../state-stores/modal-state.state';

/**
 * Component that displays and manages the user's profile.
 * - Fetches the user's data on init
 * - Allows updating user details
 * - Enables password change functionality
 */
@Component({
  selector: 'app-profile',
  imports: [CommonModule, FormsModule, NgIconComponent, SpinnerComponent, TranslocoPipe],
  templateUrl: './profile.component.html',
  providers: [provideIcons({ matEdit, matCloseOutline, matErrorOutline })],
})
export class ProfileComponent implements OnInit {

  /**
   * Callback function triggered when modal should be closed
   * @param void
   * @returns void
   */
  @Input() onCancel: Function = () => {};

  /**
   * Callback function triggered after successful save
   * @param void  
   * @returns void
   */
  @Input() onSave: Function = () => {};

  /**
   * Whether the modal is currently loading
   */
  @Input() loading: boolean = false;

  /**
   * The user model containing personal and address information.
   */
  public user: User = {
    id: -1,
    firstName: '',
    lastName: '',
    phone: '',
    phone2: '',
    email: '',
    rowVer: [],
    address: {
      id: 0,
      postalCode: '',
      street1: '',
      street2: '',
      rowVer: [],
      district: {
        name: '',
      },
      country: {
        name: '',
      },
    },
  };

  /**
   * Holds old, new, and confirmation password values for password change.
   */
  protected userPassword: any = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  /**
   * Whether the password change fields are visible.
   */
  protected showPasswordFields: boolean = false;

  /**
   * Whether a password change request is in progress.
   */
  protected isChangingPassword: boolean = false;

  /**
   * Password update feedback shown below password fields.
   */
  protected passwordChangeMessage: string | null = null;

  /**
   * Password update error feedback shown below password fields.
   */
  protected passwordChangeError: string | null = null;

  /**
   * Whether to show password change success modal.
   */
  protected showPasswordChangeSuccess: boolean = false;

  /**
   * Whether to show password change error modal.
   */
  protected showPasswordChangeError: boolean = false;

  /**
   * Whether profile data is currently loading.
   */
  protected isProfileDataLoading: boolean = true;

  /**
   * Whether to show profile load error modal.
   */
  protected showProfileLoadError: boolean = false;

  /**
   * Error message shown when profile data fails to load.
   */
  protected profileLoadErrorMessage: string = 'Failed to load profile data.';

  /**
   * Available countries for address selection.
   */
  protected countries: Country[] = [];

  /**
   * Available districts for selected country.
   */
  protected districts: District[] = [];

  /**
   * Original user data to track changes
   */
  private originalUser: User | null = null;

  /**
   * Original password data to track changes
   */
  private originalUserPassword: any = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  /**
   * Whether to show the cancel confirmation popup
   */
  protected showCancelConfirmPopup: boolean = false;

  /**
   * Error message to display when save fails
   */
  protected saveError: string | null = null;

  /**
   * Whether to show the save success popup
   */
  protected showSuccessMessage: boolean = false;

  /**
   * Whether the cancel popup is fading out
   */
  protected isCancelPopupFading: boolean = false;

  /**
   * Whether the user has a profile picture
   */
  protected hasProfilePicture: boolean = false;

  /**
   * Profile picture URL
   */
  protected profilePictureUrl: string = '';

  /**
   * Constructs the component with required services.
   * 
   * @param authService MSAL authentication service for acquiring tokens
   * @param userService Service to fetch and update user data
   * @param grapAPIService Service to interact with Microsoft Graph API
   * @param modalState Service to manage modal open state
   */
  constructor(
    private readonly authService: MsalService,
    private readonly userService: UserService,
    private readonly grapAPIService: MSGraphAPIService,
    private readonly modalState: ModalState
  ) {}

  /**
   * Lifecycle hook that runs after component initialization.
   * Fetches user data from the backend.
   */
  ngOnInit(): void {
    // Set modal state to open
    this.modalState.setModalOpen(true);
    this.isProfileDataLoading = true;
    this.showProfileLoadError = false;

    this.userService
      .getUserByOid()
      .subscribe({
        next: (result) => {
          void this.handleProfileDataLoaded(result);
        },
        error: (error) => {
          console.error('Error loading profile data:', error);
          this.profileLoadErrorMessage =
            error instanceof Error && error.message
              ? error.message
              : 'Failed to load profile data.';
          this.showProfileLoadError = true;
          this.isProfileDataLoading = false;
        },
      });
  }

  /**
   * Handles successful profile query responses and initializes dependent data.
   */
  private async handleProfileDataLoaded(result: any): Promise<void> {
    try {
      this.user = cloneDeep(result.data.userBySSOLink);
      this.ensureAddressInitialized();
      await this.loadAddressLookups();
      // Store original data for dirty checking
      this.originalUser = cloneDeep(this.user);
      this.originalUserPassword = cloneDeep(this.userPassword);

      // Set profile picture state
      this.hasProfilePicture = !!this.profilePictureUrl;
    } catch (error) {
      console.error('Error initializing profile data:', error);
      this.profileLoadErrorMessage =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to load profile data.';
      this.showProfileLoadError = true;
    } finally {
      this.isProfileDataLoading = false;
    }
  }

  /**
   * Returns the label for the district field based on selected country.
   */
  getDistrictLabel(): string {
    return this.user.address?.country?.districtType?.name ?? 'Province/State';
  }

  /**
   * Returns the label for the postal code field based on selected country.
   */
  getPostalCodeLabel(): string {
    return this.user.address?.country?.postalCodeType?.name ?? 'Postal Code / Zip Code';
  }

  /**
   * Handles country dropdown changes and refreshes districts.
   */
  async onCountryChange(newCountryId: number | string): Promise<void> {
    const parsedCountryId = Number(newCountryId);
    if (!this.user.address) {
      return;
    }

    this.user.address.countryId = Number.isNaN(parsedCountryId) ? 0 : parsedCountryId;
    this.user.address.country =
      this.countries.find((country) => country.id === this.user.address?.countryId) ?? null;
    this.user.address.districtId = 0;
    this.user.address.district = null;

    if (!this.user.address.countryId) {
      this.districts = [];
      return;
    }

    this.districts = await this.userService.getDistricsByCountry(this.user.address.countryId);
  }

  /**
   * Handles district dropdown changes.
   */
  onDistrictChange(districtId: number | string): void {
    const parsedDistrictId = Number(districtId);
    if (!this.user.address) {
      return;
    }

    this.user.address.districtId = Number.isNaN(parsedDistrictId) ? 0 : parsedDistrictId;
    this.user.address.district =
      this.districts.find((district) => district.id === this.user.address?.districtId) ?? null;
  }

  /**
   * Checks if the new password and confirmation password match.
   * 
   * @returns `true` if passwords match, `false` otherwise.
   */
  checkPasswordMatch(): boolean {
    return this.userPassword.newPassword === this.userPassword.confirmPassword;
  }

  /**
   * Attempts to change the user's password via Microsoft Graph API.
   * Acquires an access token and submits the password change request.
   * Displays alerts on success or error.
   */
  changePassword(): void {
    this.showPasswordChangeSuccess = false;
    this.showPasswordChangeError = false;
    this.passwordChangeMessage = null;
    this.passwordChangeError = null;

    if (!this.checkPasswordMatch()) {
      this.passwordChangeError = 'Passwords do not match';
      return;
    }

    if (!this.userPassword.oldPassword || !this.userPassword.newPassword) {
      this.passwordChangeError = 'Current and new passwords are required';
      return;
    }

    this.isChangingPassword = true;

    this.authService.instance
      .acquireTokenSilent({
        scopes: environment.apiConfig.scopes,
      })
      .then((result) => {
        this.grapAPIService
          .changePassword(
            this.userPassword.oldPassword,
            this.userPassword.newPassword,
            result.accessToken
          )
          .subscribe({
            next: () => {
              this.userPassword = {
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
              };
              // Update original password state after successful change
              this.originalUserPassword = cloneDeep(this.userPassword);
              this.showPasswordFields = false;
              this.passwordChangeMessage = 'Your password has been changed successfully.';
              this.showPasswordChangeSuccess = true;
              this.isChangingPassword = false;
            },
            error: (error) => {
              console.error('Error changing password:', error);
              this.passwordChangeError =
                error instanceof Error && error.message
                  ? error.message
                  : 'Error changing password';
              this.showPasswordChangeError = true;
              this.isChangingPassword = false;
            },
          });
      })
      .catch((error) => {
        console.error('Error acquiring token for password change:', error);
        this.passwordChangeError = 'Error changing password';
        this.showPasswordChangeError = true;
        this.isChangingPassword = false;
      });
  }

  /**
   * Cancels the password change process and clears input fields.
   */
  cancelPasswordChange(): void {
    if (this.isChangingPassword) {
      return;
    }

    this.showPasswordFields = false;
    this.userPassword.oldPassword = '';
    this.userPassword.newPassword = '';
    this.userPassword.confirmPassword = '';
    // Reset to original password state
    this.userPassword = cloneDeep(this.originalUserPassword);
    this.passwordChangeError = null;
    this.passwordChangeMessage = null;
    this.showPasswordChangeSuccess = false;
    this.showPasswordChangeError = false;
  }

  /**
   * Dismisses the password change success modal.
   */
  closePasswordChangeSuccess(): void {
    this.showPasswordChangeSuccess = false;
    this.passwordChangeMessage = null;
  }

  /**
   * Dismisses the password change error modal.
   */
  closePasswordChangeError(): void {
    this.showPasswordChangeError = false;
  }

  /**
   * Closes the profile load error modal and profile form.
   */
  closeProfileLoadError(): void {
    this.showProfileLoadError = false;
    this.modalState.setModalOpen(false);
    this.onCancel();
  }

  /**
   * Submits the user form and updates the user profile via the user service.
   */
  onSubmit = async (): Promise<void> => {
    this.saveError = null;
    try {
      this.user = await this.userService.updateProfile(this.user);
      // Update original data after successful save
      this.originalUser = cloneDeep(this.user);
      // Call the onSave callback
      this.onSave();
      // Show success popup — closeSuccessMessage() will close the form
      this.showSuccessMessage = true;
    } catch (error) {
      console.error('Error saving profile:', error);
      this.saveError = error instanceof Error ? error.message : 'Error saving profile';
    }
  };

  /**
   * Closes the success message popup and then closes the profile modal
   */
  closeSuccessMessage(): void {
    this.showSuccessMessage = false;
    this.modalState.setModalOpen(false);
    this.onCancel();
  }

  /**
   * Checks if the form has unsaved changes
   * @returns true if there are unsaved changes
   */
  isDirty(): boolean {
    if (!this.originalUser) return false;
    
    // Check user data changes
    const userChanged = JSON.stringify(this.user) !== JSON.stringify(this.originalUser);
    
    // Check password data changes
    const passwordChanged = JSON.stringify(this.userPassword) !== JSON.stringify(this.originalUserPassword);
    
    return userChanged || passwordChanged;
  }

  /**
   * Handles cancel button click
   * If form is dirty, shows confirmation popup
   * Otherwise, closes the modal immediately
   */
  onCancelClicked(): void {
    if (this.isDirty()) {
      this.showCancelConfirmPopup = true;
      this.isCancelPopupFading = false;
    } else {
      this.resetForm();
      this.modalState.setModalOpen(false);
      this.onCancel();
    }
  }

  /**
   * Confirms canceling and discards changes
   */
  confirmCancel(): void {
    this.isCancelPopupFading = true;
    setTimeout(() => {
      this.showCancelConfirmPopup = false;
      this.isCancelPopupFading = false;
      this.resetForm();
      this.modalState.setModalOpen(false);
      this.onCancel();
    }, 300);
  }

  /**
   * Dismisses the cancel confirmation popup
   */
  dismissCancelPopup(): void {
    this.isCancelPopupFading = true;
    setTimeout(() => {
      this.showCancelConfirmPopup = false;
      this.isCancelPopupFading = false;
    }, 300);
  }

  /**
   * Resets the form to original values
   */
  private resetForm(): void {
    if (this.originalUser) {
      this.user = cloneDeep(this.originalUser);
    }
    this.userPassword = cloneDeep(this.originalUserPassword);
    this.showPasswordFields = false;
  }

  /**
   * Ensures the user address object is available for template bindings.
   */
  private ensureAddressInitialized(): void {
    if (!this.user.address) {
      this.user.address = {
        id: 0,
        postalCode: '',
        street1: '',
        street2: '',
        city: '',
        district: null,
        country: null,
        districtId: 0,
        countryId: 0,
      };
      return;
    }

    this.user.address.countryId = this.user.address.countryId ?? this.user.address.country?.id ?? 0;
    this.user.address.districtId = this.user.address.districtId ?? this.user.address.district?.id ?? 0;
  }

  /**
   * Loads address lookup data and syncs selected country/district.
   */
  private async loadAddressLookups(): Promise<void> {
    this.countries = await this.userService.getCountries();
    const countryId = this.user.address?.countryId ?? this.user.address?.country?.id ?? 0;
    if (!countryId) {
      this.districts = [];
      return;
    }

    this.user.address!.country =
      this.countries.find((country) => country.id === countryId) ?? this.user.address!.country;

    this.districts = await this.userService.getDistricsByCountry(countryId);

    const districtId = this.user.address?.districtId ?? this.user.address?.district?.id ?? 0;
    this.user.address!.district =
      this.districts.find((district) => district.id === districtId) ?? this.user.address!.district;
  }

  /**
   * Warns user before leaving page with unsaved changes
   */
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(event: BeforeUnloadEvent): boolean {
    if (this.isDirty()) {
      event.preventDefault();
      event.returnValue = '';
      return false;
    }
    return true;
  }

  /**
   * Handle ESC key to close modal
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    // Ensure the event is a KeyboardEvent
    if (event instanceof KeyboardEvent && !this.showCancelConfirmPopup) {
      this.onCancelClicked();
    }
  }

  /**
   * Gets the profile picture URL or returns a default
   */
  getProfilePictureUrl(): string {
    if (this.profilePictureUrl) {
      return this.profilePictureUrl;
    }
    // You can integrate with Microsoft Graph API to get the user's photo
    // For now, return a placeholder or the user's initials
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(this.user.firstName + ' ' + this.user.lastName)}&size=200&background=3b82f6&color=ffffff&bold=true`;
  }

  /**
   * Handles profile picture error (fallback to initials)
   */
  onImageError(event: Event): void {
    this.hasProfilePicture = false;
    const imgElement = event.target as HTMLImageElement;
    imgElement.style.display = 'none';
  }

  /**
   * Gets user initials for avatar fallback
   */
  getInitials(): string {
    const firstName = this.user.firstName?.charAt(0)?.toUpperCase() || '';
    const lastName = this.user.lastName?.charAt(0)?.toUpperCase() || '';
    return firstName + lastName || 'U';
  }
}
